import { Entry } from 'contentful'
import { IArticleFields, IPodcastEpisodeFields, IHelpArea } from '@estercare/ester-shared'
import { HealthPlanOutcome } from '../api/ester-b2b-api/private/healthPlan/types'

// Keyed on the Swedish outcome title exactly as ester-backoffice-api renders it, which computes
// titles at read time - so a rename there changes every existing health plan at once. A title with
// no entry here is skipped silently, costing the user their related content with no error
// anywhere, which is why superseded and variant spellings are kept rather than replaced.
const outcomeHelpAreaMapping: Record<string, string[]> = {
  Klimakteriet: ['Klimakteriet'],
  Perimenopaus: ['Klimakteriet'],
  Mensvärk: ['Mens'],
  'Oregelbunden cykel': ['Mens'],
  'Rikliga blödningar': ['Mens'],
  'PMS/PMDS': ['PMS'],
  // Two names for one outcome: renamed in ester-backoffice-api #519, and since titles are
  // computed at read time there, plans exist in the wild under both.
  'PMS med preventivmedel': ['PMS'],
  'Hormonella besvär med preventivmedel': ['PMS'],
  'PMOS (PCOS)': ['PMOS (PCOS)', 'Hormoner'],
  'PMOS (PCOS) & Fertilitet': ['PMOS (PCOS)', 'Fertilitet'],
  Endometrios: ['Endometrios'],
  'Fertilitet - med råd till manlig partner': ['Fertilitet'],
  Fertilitet: ['Fertilitet'],
  Graviditet: ['Graviditet'],
  'Post-partum': ['Efter födseln'],
  Förlossningsskador: ['Efter födseln'],
  Sköldkörtelsjukdom: ['Skölkörteln', 'Hormoner'],
  // These two look identical but differ in one character: en dash (U+2013) first, ASCII hyphen
  // (U+002D) second. ester-backoffice-api emits the hyphen; the en dash predates that and never
  // matched. Both stay so either spelling resolves. Do not "de-duplicate" them.
  'Sexlust – hög eller låg lust till intimitet': ['Sex och Sexualitet'],
  'Sexlust - hög eller låg lust till intimitet': ['Sex och Sexualitet'],
  'Vaginal svampinfektion': ['Infektioner'],
  'Bakteriell vaginos': ['Infektioner'],
  'Rikliga flytningar': ['Infektioner'],
  'Torra eller sköra slemhinnor': ['Klimakteriet'],
  Vulvodyni: ['Sex och Sexualitet'],
  Ansträngningsinkontinens: ['Efter födseln'],
  Trängningsinkontinens: ['Efter födseln'],
  Blandinkontinens: ['Efter födseln'],
  Överfyllnadsinkontinens: ['Efter födseln'],
  'Överaktiv blåsa': ['Efter födseln'],
}

const getHelpAreasForOutcomes = (outcomes: HealthPlanOutcome[]): string[] => {
  const helpAreas = new Set<string>()
  for (const outcome of outcomes) {
    const title = outcome.title || outcome.outcomeId
    const areas = outcomeHelpAreaMapping[title]
    if (areas) {
      for (const area of areas) {
        helpAreas.add(area)
      }
    }
  }
  return Array.from(helpAreas)
}

const getEntryHelpAreas = (helpAreas: IHelpArea[] | undefined): string[] => {
  if (!Array.isArray(helpAreas)) return []
  return helpAreas.map((area) => area.fields.name).filter((name): name is string => !!name)
}

const scoreEntry = (entry: Entry<IArticleFields | IPodcastEpisodeFields>, relevantHelpAreas: string[]): number => {
  const entryHelpAreas = getEntryHelpAreas(entry.fields.relatedHelpAreas)
  return entryHelpAreas.filter((area) => relevantHelpAreas.includes(area)).length
}

export const findRelatedContentBasedOnOutcome = <T extends IArticleFields | IPodcastEpisodeFields>(
  entries: Entry<T>[],
  outcomes: HealthPlanOutcome[] | undefined
): { matched: Entry<T>[]; unmatched: Entry<T>[] } => {
  if (!outcomes || outcomes.length === 0) return { matched: [], unmatched: entries }

  const relevantHelpAreas = getHelpAreasForOutcomes(outcomes)
  if (relevantHelpAreas.length === 0) return { matched: [], unmatched: entries }

  const matched = entries.filter((entry) => scoreEntry(entry, relevantHelpAreas) > 0)
  const unmatched = entries.filter((entry) => scoreEntry(entry, relevantHelpAreas) === 0)

  return { matched, unmatched }
}
